// EUR conversion for a foreign-currency invoice. Whichever is set wins: `settled_amount_eur`
// (the bank credit), then `fixed_amount_eur`, then `exchange_rate`. Everything else is derived.

/** The EUR the invoice should be worth before the bank has paid: the fixed amount, else
 *  gross × the document's rate. */
export function expectedEur(invoice) {
    const fixed = invoice?.fixed_amount_eur;
    if (fixed != null) return Number(fixed);
    return (Number(invoice?.total_gross) || 0) * (Number(invoice?.exchange_rate) || 1);
}

/** Which of the two conversion inputs this invoice uses: 'amount' or 'rate'. */
export function conversionMode(invoice) {
    return invoice?.fixed_amount_eur != null ? 'amount' : 'rate';
}

/** The rate to convert this invoice: the effective one once the credit is known, else the
 *  rate agreed on the document. */
export function eurRate(invoice) {
    const gross = Number(invoice?.total_gross) || 0;
    // A rate can only be derived from an amount if there is a gross to divide by;
    // gross === 0 falls through to the document's own rate.
    if (gross > 0) {
        const settled = invoice?.settled_amount_eur;
        if (settled != null) return Number(settled) / gross;
        const fixed = invoice?.fixed_amount_eur;
        if (fixed != null) return Number(fixed) / gross;
    }
    return Number(invoice?.exchange_rate) || 1;
}

/** Convert one of this invoice's amounts (net, gross, …) to EUR. */
export function eurAmount(value, invoice) {
    return (Number(value) || 0) * eurRate(invoice);
}

/** Credit minus what the invoice's own rate predicted. Null, not 0, when unsettled, so a
 *  caller can tell "no data" from "no difference". */
export function fxDifference(invoice) {
    const settled = invoice?.settled_amount_eur;
    if (settled == null) return null;
    // Measured against the fixed amount if one was agreed, otherwise the document's rate.
    return Number(settled) - expectedEur(invoice);
}

/** True when the invoice is settled at a rate other than its own (to the cent). */
export function hasFxCorrection(invoice) {
    const diff = fxDifference(invoice);
    return diff != null && Math.abs(diff) >= 0.005;
}
